// Hatha Yoga Warminster - Service + Location Page (SEO Enhanced)
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car, Check, Users, Clock, Heart } from "lucide-react";
import Navigation from "../../../components/shared/Navigation";
import Footer from "../../../components/shared/Footer";
import PageBreadcrumb from "../../../components/shared/PageBreadcrumb";
import { usePageMeta, generateServiceSchema, generateFAQSchema, SchemaScript } from "../../../components/shared/SEOHelper";

const HathaYogaWarminster = () => {
  usePageMeta({
    title: "Hatha Yoga Warminster | Yoga Classes | Annelie Lily",
    description: "Hatha yoga classes for Warminster residents. Traditional yoga for strength and flexibility, based in Frome, 20 minutes from Warminster. All levels welcome.",
    canonical: "https://annelielily.com/areas/yoga-warminster/hatha-yoga"
  });

  const serviceSchema = generateServiceSchema({
    name: "Hatha Yoga Classes for Warminster",
    description: "Hatha yoga classes for Warminster residents at Annelie Lily Yoga in Frome. Traditional practice building strength, flexibility, and calm.",
    serviceType: "Yoga Classes",
    url: "https://annelielily.com/areas/yoga-warminster/hatha-yoga"
  });

  const faqs = [
    { question: "Where are classes held?", answer: "All classes are at my studio in Frome, about 20 minutes from Warminster via the A350 and A362." },
    { question: "What is Hatha yoga?", answer: "Traditional yoga combining postures, breathing, and relaxation. It builds strength and flexibility while calming the mind. Suitable for all levels." },
    { question: "Is it suitable for beginners?", answer: "Yes. I offer modifications for every pose, so you can work at your own level. Many students started as complete beginners." },
    { question: "What should I bring?", answer: "Just comfortable clothing you can move in. Mats and all props are provided, though you're welcome to bring your own mat." },
    { question: "How often should I practice?", answer: "Once a week is a great start. Many students find twice weekly helps them progress faster and maintain benefits between sessions." },
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Hatha Yoga" currentSlug="areas/yoga-warminster/hatha-yoga" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Hatha Yoga for Warminster</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Hatha Yoga Warminster</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              Traditional Hatha yoga for Warminster residents. Build strength, flexibility, and calm in small classes just 20 minutes from town.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />20 min from Warminster</span>
            </div>
            <Link to="/contact" className="btn-primary" data-testid="hero-cta">Book a Hatha Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Snippet / Direct Answer */}
      <section className="py-10 px-6 md:px-12 bg-cream border-b border-gold/10">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl text-neutral-800 mb-3">What is Hatha yoga?</h2>
          <p className="text-neutral-700">
            Hatha yoga is traditional yoga combining physical postures (asanas), breathing techniques (pranayama), and relaxation. It's the foundation of most modern yoga, building strength and flexibility at a pace that suits your body. All levels welcome.
          </p>
        </div>
      </section>

      {/* Who Is This For */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">Who is Hatha yoga for?</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: "Complete beginners", desc: "Learn proper foundations with clear guidance and modifications" },
              { title: "Those seeking balance", desc: "Equal focus on strength, flexibility, breathing, and relaxation" },
              { title: "Desk workers", desc: "Counter the effects of sitting — rounded shoulders, tight hips, stiff back" },
              { title: "Anyone wanting calm", desc: "Every class ends with guided relaxation to quiet the mind" },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{item.title}</h3>
                <p className="text-neutral-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8">What to expect in a Hatha class</h2>
          <div className="space-y-6 text-neutral-700">
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Centering & breath</h3>
                <p className="text-sm">We begin by settling in, leaving the day behind, connecting with the breath.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Standing poses</h3>
                <p className="text-sm">Building strength, balance, and grounding. Variations offered for every level.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Floor work</h3>
                <p className="text-sm">Seated stretches, twists for the spine, gentle inversions. This is where deep release happens.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-gold font-semibold text-sm">4</span>
              </div>
              <div>
                <h3 className="font-heading text-lg text-neutral-800 mb-1">Deep relaxation</h3>
                <p className="text-sm">Every class ends with guided relaxation — time for body and mind to integrate the practice.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Why Warminster Residents Choose Us */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Why Warminster residents travel to Frome</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              <Link to="/areas/yoga-warminster" className="text-gold hover:underline">Warminster</Link> to Frome is about 20 minutes through the Wiltshire countryside. If you're looking for Hatha yoga with personal attention, my small classes offer something different from gym yoga.
            </p>
            <p>
              Classes are small (6-8 people) so I can offer individual guidance. <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline">New to yoga?</Link> That's fine — I welcome all levels and provide modifications throughout.
            </p>
            <p>
              For something slower, try <Link to="/areas/yoga-warminster/yin-yoga" className="text-gold hover:underline">Yin yoga for Warminster</Link>. For one-to-one attention, <Link to="/areas/yoga-warminster/private-yoga" className="text-gold hover:underline">private sessions</Link> are available.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Benefits of Hatha Yoga</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Users, text: "Small group attention" },
              { icon: Clock, text: "Work at your pace" },
              { icon: Heart, text: "Reduces stress" },
              { icon: Check, text: "All levels welcome" },
            ].map((item, i) => (
              <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10 flex items-center gap-3">
                <item.icon className="w-5 h-5 text-gold flex-shrink-0" />
                <span className="text-neutral-700">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">Questions About Hatha Yoga in Warminster</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-neutral-700 mb-4">
            I'm Annelie, a qualified yoga teacher offering small, welcoming classes where everyone gets individual attention. 
            <Link to="/about" className="text-gold hover:underline ml-1">Learn more about my approach</Link>, or 
            <Link to="/about" className="text-gold hover:underline ml-1">read what students say</Link>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to Start?</h2>
          <p className="text-neutral-600 mb-6">Get in touch and I'll help you find the right class.</p>
          <Link to="/contact" className="btn-primary" data-testid="bottom-cta">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Also available for Warminster residents:</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/areas/yoga-warminster/yin-yoga" className="text-sm text-gold hover:underline">Yin Yoga</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-warminster/private-yoga" className="text-sm text-gold hover:underline">Private Sessions</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/areas/yoga-warminster" className="text-sm text-gold hover:underline">All Classes</Link>
            <span className="text-neutral-300">|</span>
            <Link to="/hatha-yoga" className="text-sm text-gold hover:underline">Hatha Yoga (Main Page)</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HathaYogaWarminster;
